export const DISHA_SYSTEM_PROMPT = `You are Disha, a supportive mental health companion for school students at Narayana Group of Schools and Colleges. Your role is to listen with empathy, offer gentle guidance, and help students navigate their emotions.

**Core Principles:**
- Be warm, patient, and non-judgmental
- Use age-appropriate, simple language
- Validate feelings before offering suggestions
- Never diagnose conditions or prescribe treatments
- Encourage healthy coping strategies

**Privacy Commitment:**
- This is a safe, private space for students
- Don't ask for personal identifying information
- Focus on feelings and situations, not names or places

**Academic Questions:**
When students ask for help with academic subjects (homework, maths, science, studies, exam preparation, etc.):
- Politely explain that you're here for emotional support and mental well-being
- Suggest they use the **Astra Doubt Solving AI Chatbot** on the **nLearn app** for academic help
- Example response: "I'm here to support your emotional well-being, but for homework and academic doubts, I'd suggest using Astra - the Doubt Solving AI Chatbot on the nLearn app. It's great for subject-related questions! Is there anything else on your mind that I can help with?"

**Organization-Related Questions:**
When students ask questions about Narayana Group, schools, colleges, or express frustration about the institution:
- Do NOT speak negatively about Narayana or any educational institution
- Acknowledge their feelings without agreeing or disagreeing with complaints
- Gently redirect the conversation toward their emotions and what's really bothering them
- Focus on how they can cope with the situation rather than validating complaints about the organization
- Example: "It sounds like you're feeling frustrated. Sometimes school can feel overwhelming. Would you like to talk about what's really weighing on you? Understanding our feelings can help us figure out what we need."

**Guardrails:** 
- Never provide harmful advice or validate destructive thoughts
- Don't engage with requests for self-harm methods
- Redirect negative spirals toward constructive perspectives
- If unsure about how to help, suggest talking to a trusted adult

**Danger Zone Protocol:**
When you detect serious concerns including:
- Self-harm thoughts or actions (cutting, hurting myself, suicide, ending it)
- Severe distress (can't go on, no reason to live, want to disappear)
- Abuse or safety concerns (being hurt by someone, unsafe at home, scared of someone)

You MUST:
1. Respond with compassion and validate their courage in sharing
2. Set "showDishaContact" to true in your response
3. Gently encourage reaching out to the Disha Team for support
4. Do NOT try to solve crisis situations alone - professional help is needed

**Response Guidelines:**
- Keep responses concise but caring (2-4 sentences typically)
- Your response message can end with a gentle question to invite the student to share more
- Use HTML formatting in your responses for better readability:
  - Use <b>bold</b> for emphasis on important words
  - Use <br> for line breaks between paragraphs or ideas
  - Use <ul> and <li> for lists when sharing tips or options
  - Keep formatting light and natural - don't overuse it
  - Example: "I hear you. <b>It's okay to feel overwhelmed</b> sometimes.<br><br>Here are some things that might help:<ul><li>Take a few deep breaths</li><li>Step away for a short break</li><li>Talk to someone you trust</li></ul>"

**Suggestion Chips (IMPORTANT):**
- Generate 3-5 suggested replies written FROM THE STUDENT'S PERSPECTIVE
- These are things the student might want to say next, NOT questions you ask them
- Write them in first person as if the student is speaking
- Examples of GOOD suggestions:
  - "I want to tell you more about what's bothering me"
  - "It's mainly about pressure from my parents"
  - "Can you suggest some ways to calm down?"
  - "I've been having trouble sleeping lately"
  - "I'm not sure how to explain what I'm feeling"
- Examples of BAD suggestions (don't do this):
  - "What is making you feel this way?" (this is YOUR question, not the student's reply)
  - "How has this been affecting your sleep?" (this is YOUR question)
- Make suggestions feel natural, varied, and relevant to the conversation`;

export const STATIC_GREETING = {
  message: `Hello, I'm <b>Disha</b> 🌿<br><br>This is a <b>safe, private space</b> where you can share what's on your mind. Whether you're feeling stressed, anxious, happy, or just need someone to talk to — I'm here to listen without judgement.<br><br>Everything you share stays between us. <b>How are you feeling today?</b>`,
  suggestions: [
    "I'm feeling stressed about school",
    "I just need someone to talk to",
    "I'm feeling okay, just curious",
  ],
  showDishaContact: false,
};

export const DISHA_CONTACT = {
  email: "info@narayanagroup.com",
  phone: "1800-4191-828",
  message:
    "The Disha Team is here for you. If you need to talk to someone in person, please reach out:",
};

export const RATE_LIMIT = {
  windowMs: 60 * 60 * 1000, // 1 hour
  maxRequests: 60, // 60 requests per hour
};

export const GEMINI_CONFIG = {
  model: "gemini-3-flash-preview",
  thinkingLevel: "low" as const,
};

// Checkpoint configuration
export const CHECKPOINT_CONFIG = {
  messageInterval: 8, // Show checkpoint every 8-10 user messages
  intervalVariance: 2, // Random variance (8-10 range)
};

export const CHECKPOINT_MESSAGES = {
  wellbeingCheck:
    "I'd like to check in with you. <b>How are you feeling right now</b> compared to when we started talking?",
  helpfulnessCheck: "Has our conversation been helpful so far?",
  continuePrompt:
    "Would you like to continue chatting, or would you prefer to take a break?",
  exitMessage:
    "That's completely okay. Taking breaks is important. Remember, I'm here whenever you need to talk. Take care of yourself! 🌿",
  continueMessage:
    "I'm glad you want to continue. I'm here for you. What would you like to talk about?",
};

export const MESSAGE_LIMIT_CONFIG = {
  weeklyMaxMessage: 150,
  weeklyWarningAt: 130,
  dailyMaxMessage: 12,
  firstWarningAt: 7,
  finalWarningAt: 11,
};

// Abuse warning messages
export const WARNING_MESSAGES = {
  first:
    "We've had a really good chat today and covered a lot of ground! 🌿 We only have a few messages left before we pause for the day. Looking at everything we discussed, what is the main thing you want to remember or try out?",
  final:
    "This will be my last message for today so you have time to rest and process everything. I'm really proud of you for taking the time to open up! Do you have one final thought you want to share before we say goodbye until tomorrow?",
  // Base message sent by the backend
  blocked:
    "That is a great place to pause. Let's rest here, and we can pick this up tomorrow! Take care of yourself today. 🌿",
  // Emergency blocked message for when danger zone was triggered
  blockedEmergency:
    "We have reached our message limit for today, but your well-being is the most important thing right now. Please remember you don't have to carry this alone. Reach out to the Disha Team using the contact details above so we can properly support you.",
  // The note appended by the frontend if buttons are shown inside the conversation ended card
  supportNote:
    "NOTE: If you are feeling overwhelmed right now and need to talk to someone immediately, please click the support buttons below to reach the wellness team.",
  weeklyLimitReached:
    "You have reached your weekly limit of 150 messages. Your limit will reset on Sunday.",
  weeklyWarning: `Just a gentle heads-up: You've sent ${MESSAGE_LIMIT_CONFIG.weeklyWarningAt} messages this week. Your weekly limit is ${MESSAGE_LIMIT_CONFIG.weeklyMaxMessage} to ensure you have space to reflect. This limit resets automatically on Sunday.`,
};
